import type { MouseEvent as ReactMouseEvent } from 'react';
import type { PackBuilderItem } from '../../../lib/api/operatorConsole';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Pill } from '../../../components/ui/Pill';
import { formatMoney, formatRange } from '../packBuilderFormat';
import { CardThumb } from './CardThumb';
import { engagementStatusBadge } from './engagementStatus';

interface Props {
  item: PackBuilderItem;
  selected: boolean;
  currency: string | null;
  onSelectChange: (item: PackBuilderItem, checked: boolean) => void;
}

const CELL = 'px-3 py-2 align-middle text-[12px] text-[var(--co-text)]';

/** One card of the pack contents. The status pill is the point of this row:
 *  it tells the operator why a card is not in play without pretending it left
 *  the pack. */
export function PackItemRow({ item, selected, currency, onSelectChange }: Props) {
  const meta = [item.year, item.set, item.player]
    .filter((value) => value !== null && value !== '')
    .join(' · ');
  const grade =
    item.grade && (item.grade.company || item.grade.grade)
      ? `${item.grade.company ?? ''} ${item.grade.grade ?? ''}`.trim()
      : 'Raw';
  const status = engagementStatusBadge(item.engagement_status);

  // The whole row is the hit target. A drag that selects text must NOT toggle,
  // so the click is ignored when the user left a selection behind, and clicks
  // that originate inside the cell's own control are left to that control.
  const toggleFromRow = (event: ReactMouseEvent<HTMLTableRowElement>) => {
    if (window.getSelection()?.toString()) return;
    if ((event.target as HTMLElement).closest('input,button,a,label')) return;
    onSelectChange(item, !selected);
  };

  return (
    <tr
      className={`cursor-pointer border-b border-[var(--co-border)] last:border-b-0 ${
        selected ? 'bg-[var(--co-bg)]' : ''
      }`}
      onClick={toggleFromRow}
    >
      <td className={CELL}>
        <Checkbox
          aria-label={`Select ${item.title}`}
          checked={selected}
          onChange={(checked) => onSelectChange(item, checked)}
        />
      </td>
      <td className={CELL}>
        <div className="flex items-center gap-3">
          <CardThumb src={item.image_url} title={item.title} />
          <div className="min-w-0">
            {/* Wraps to two lines like the picker's row: the grade lives at the
                end of the title and a single clipped line hid it. */}
            <div className="line-clamp-2 break-words font-medium" title={item.title}>
              {item.title}
            </div>
            <div className="truncate text-[11px] text-[var(--co-muted)]">{meta || '—'}</div>
          </div>
        </div>
      </td>
      <td className={`${CELL} whitespace-nowrap`}>{grade}</td>
      <td className={`${CELL} whitespace-nowrap text-right font-mono`}>
        {formatMoney(item.fmv, currency)}
      </td>
      <td className={`${CELL} whitespace-nowrap font-mono text-[var(--co-muted)]`}>
        {item.bucket ? formatRange(item.bucket) : '—'}
      </td>
      <td className={CELL}>
        <div className="flex flex-wrap gap-1">
          <Pill tone={status.tone} {...(status.hint ? { title: status.hint } : {})}>
            {status.label}
          </Pill>
          <Pill tone={item.ownership === 'mine' ? 'brand' : 'neutral'}>
            {item.ownership === 'mine' ? 'Mine' : 'Shared'}
          </Pill>
        </div>
      </td>
    </tr>
  );
}
